import { useState, useEffect, useCallback } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import {
  Archive, Plus, Trash, ToggleLeft, ToggleRight,
  SpinnerGap, MagnifyingGlass, FileText, PencilSimple,
  Check, X, CloudArrowUp
} from "@phosphor-icons/react";

// ── Tag Input ──────────────────────────────────────────────
function TagInput({ tags, onChange, maxTags = 5 }) {
  const [input, setInput] = useState("");
  const addTag = () => {
    const trimmed = input.trim().toLowerCase();
    if (trimmed && !tags.includes(trimmed) && tags.length < maxTags) {
      onChange([...tags, trimmed]);
      setInput("");
    }
  };
  return (
    <div>
      <div className="flex flex-wrap gap-1.5 mb-2">
        {tags.map((t) => (
          <span key={t} className="flex items-center gap-1 text-xs bg-[var(--accent-subtle)] text-[#FF4400] px-2 py-1 rounded-full">
            {t}
            <button onClick={() => onChange(tags.filter((x) => x !== t))} className="hover:text-[var(--text-primary)]"><X size={10} weight="bold" /></button>
          </span>
        ))}
      </div>
      {tags.length < maxTags && (
        <div className="flex gap-2">
          <input className="flex-1 bg-[var(--bg-base)] border border-[var(--border-strong)] rounded-lg px-3 py-1.5 text-xs text-[var(--text-primary)] placeholder:text-[var(--text-muted)] focus:outline-none focus:ring-1 focus:ring-[#FF4400]"
            placeholder="Add tag..." value={input} onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addTag(); } }} />
          <button onClick={addTag} className="px-2 py-1 text-xs bg-[var(--bg-raised)] hover:bg-[var(--bg-hover)] text-[var(--text-secondary)] rounded-lg border border-[var(--border-strong)]"><Plus size={12} /></button>
        </div>
      )}
    </div>
  );
}

// ── Resource Card ──────────────────────────────────────────
function ResourceCard({ resource, onUpdate, onRemove }) {
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({
    display_name: resource.display_name,
    tags: resource.tags || [],
    always_include: resource.always_include,
    description: resource.description || "",
  });
  const [saving, setSaving] = useState(false);

  const save = async () => {
    setSaving(true);
    await onUpdate(resource.resource_id, form);
    setSaving(false);
    setEditing(false);
  };

  return (
    <motion.div layout initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, scale: 0.95 }}
      className="bg-[var(--bg-base)] border border-[var(--border)] rounded-lg p-4">
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-start gap-3 flex-1 min-w-0">
          <FileText size={22} className="text-[#FF4400] mt-0.5 shrink-0" />
          <div className="flex-1 min-w-0">
            {editing ? (
              <input className="w-full bg-[var(--bg-card)] border border-[var(--border-strong)] rounded px-2 py-1 text-sm text-[var(--text-primary)] mb-2 focus:outline-none focus:ring-1 focus:ring-[#FF4400]"
                value={form.display_name} onChange={(e) => setForm({ ...form, display_name: e.target.value })} />
            ) : (
              <p className="text-sm font-medium text-[var(--text-primary)] truncate">{resource.display_name}</p>
            )}
            <p className="text-xs text-[var(--text-muted)] mt-0.5">{resource.original_filename}</p>

            {editing ? (
              <div className="mt-2"><TagInput tags={form.tags} onChange={(tags) => setForm({ ...form, tags })} /></div>
            ) : (
              resource.tags?.length > 0 && (
                <div className="flex flex-wrap gap-1 mt-2">
                  {resource.tags.map((t) => <span key={t} className="text-[10px] bg-[var(--bg-hover)] text-[var(--text-muted)] px-1.5 py-0.5 rounded">{t}</span>)}
                </div>
              )
            )}

            {editing ? (
              <textarea className="w-full mt-2 bg-[var(--bg-card)] border border-[var(--border-strong)] rounded px-2 py-1 text-xs text-[var(--text-primary)] min-h-[50px] resize-y focus:outline-none focus:ring-1 focus:ring-[#FF4400]"
                placeholder="Short description..." value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
            ) : (
              resource.description && <p className="text-xs text-[var(--text-muted)] mt-1 line-clamp-2">{resource.description}</p>
            )}

            <button onClick={() => { if (editing) setForm({ ...form, always_include: !form.always_include }); }}
              className={`flex items-center gap-1.5 mt-2 text-xs ${(editing ? form.always_include : resource.always_include) ? "text-[var(--success)]" : "text-[var(--text-muted)]"}`}>
              {(editing ? form.always_include : resource.always_include) ? <ToggleRight size={18} weight="fill" /> : <ToggleLeft size={18} />}
              Always include in prompts
            </button>
            {(editing ? form.always_include : resource.always_include) && (
              <p className="text-xs text-[var(--success)] mt-1 ml-0.5" data-testid={`always-include-active-${resource.resource_id}`}>Active — included in every generation</p>
            )}
          </div>
        </div>
        <div className="flex items-center gap-1 shrink-0">
          {editing ? (
            <>
              <button onClick={save} disabled={saving} className="p-1.5 text-[var(--success)] hover:text-green-300">
                {saving ? <SpinnerGap size={16} className="animate-spin" /> : <Check size={16} />}
              </button>
              <button onClick={() => setEditing(false)} className="p-1.5 text-[var(--text-muted)] hover:text-[var(--text-primary)]"><X size={16} /></button>
            </>
          ) : (
            <>
              <button onClick={() => setEditing(true)} className="p-1.5 text-[var(--text-muted)] hover:text-[#FF4400]" data-testid={`edit-resource-${resource.resource_id}`}><PencilSimple size={16} /></button>
              <button onClick={() => onRemove(resource.resource_id)} className="p-1.5 text-[var(--text-muted)] hover:text-red-400" data-testid={`remove-resource-${resource.resource_id}`}><Trash size={16} /></button>
            </>
          )}
        </div>
      </div>
    </motion.div>
  );
}

// ── Add File Panel ─────────────────────────────────────────
function AddFilePanel({ uploadedFiles, bankFileIds, onAdd, onClose }) {
  const [search, setSearch] = useState("");
  const [selected, setSelected] = useState(null);
  const [tags, setTags] = useState([]);
  const [alwaysInclude, setAlwaysInclude] = useState(false);
  const [desc, setDesc] = useState("");
  const [adding, setAdding] = useState(false);

  const available = uploadedFiles.filter(
    (f) => !bankFileIds.includes(f.file_id) && f.original_filename.toLowerCase().includes(search.toLowerCase())
  );

  const handleAdd = async () => {
    if (!selected) return;
    setAdding(true);
    await onAdd({ file_id: selected.file_id, display_name: selected.original_filename, tags, always_include: alwaysInclude, description: desc });
    setAdding(false);
    onClose();
  };

  return (
    <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}
      className="bg-[var(--bg-raised)] border border-[var(--border-strong)] rounded-xl p-5 mb-4">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-sm font-semibold text-[var(--text-primary)]">Add File to Resource Bank</h3>
        <button onClick={onClose} className="text-[var(--text-muted)] hover:text-[var(--text-primary)]"><X size={18} /></button>
      </div>
      <div className="relative mb-3">
        <MagnifyingGlass size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--text-muted)]" />
        <input className="w-full bg-[var(--bg-base)] border border-[var(--border-strong)] rounded-lg pl-9 pr-3 py-2 text-sm text-[var(--text-primary)] placeholder:text-[var(--text-muted)] focus:outline-none focus:ring-1 focus:ring-[#FF4400]"
          placeholder="Search your uploaded files..." value={search} onChange={(e) => setSearch(e.target.value)} />
      </div>
      <div className="max-h-48 overflow-y-auto space-y-1 mb-4">
        {available.length === 0 ? (
          <p className="text-xs text-[var(--text-muted)] py-4 text-center">
            {uploadedFiles.length === 0 ? "No uploaded files yet. Upload files from any builder page first." : "All uploaded files are already in your resource bank."}
          </p>
        ) : (
          available.map((f) => (
            <button key={f.file_id} onClick={() => setSelected(f)}
              className={`w-full text-left px-3 py-2 rounded-lg text-sm flex items-center gap-2 transition-colors ${
                selected?.file_id === f.file_id ? "bg-[var(--accent-subtle)] text-[#FF4400] border border-[#FF4400]/30" : "bg-[var(--bg-base)] text-[var(--text-secondary)] border border-transparent hover:border-[var(--border-strong)]"
              }`}>
              <FileText size={14} />
              <span className="truncate">{f.original_filename}</span>
            </button>
          ))
        )}
      </div>
      {selected && (
        <div className="space-y-3 border-t border-[var(--border-strong)] pt-3">
          <TagInput tags={tags} onChange={setTags} />
          <textarea className="w-full bg-[var(--bg-base)] border border-[var(--border-strong)] rounded-lg px-3 py-2 text-xs text-[var(--text-primary)] min-h-[50px] resize-y placeholder:text-[var(--text-muted)] focus:outline-none focus:ring-1 focus:ring-[#FF4400]"
            placeholder="Brief description of this resource..." value={desc} onChange={(e) => setDesc(e.target.value)} />
          <button onClick={() => setAlwaysInclude(!alwaysInclude)}
            className={`flex items-center gap-1.5 text-xs ${alwaysInclude ? "text-[var(--success)]" : "text-[var(--text-muted)]"}`}>
            {alwaysInclude ? <ToggleRight size={18} weight="fill" /> : <ToggleLeft size={18} />}
            Always include in generation prompts
          </button>
          <button onClick={handleAdd} disabled={adding} data-testid="confirm-add-resource"
            className="w-full flex items-center justify-center gap-2 px-4 py-2 rounded-lg bg-[#FF4400] hover:bg-[#FF6633] text-white text-sm font-medium disabled:opacity-40 transition-colors">
            {adding ? <SpinnerGap size={14} className="animate-spin" /> : <CloudArrowUp size={16} weight="bold" />}
            Add to Resource Bank
          </button>
        </div>
      )}
    </motion.div>
  );
}

// ═══════════════════════════════════════════════════════════
// MAIN COMPONENT
// ═══════════════════════════════════════════════════════════
export const ResourceBankManager = () => {
  const { api } = useAuth();
  const [bankFiles, setBankFiles] = useState([]);
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const [bankResp, filesResp] = await Promise.all([
        api.get("/resource-bank"),
        api.get("/files"),
      ]);
      setBankFiles(bankResp.data || []);
      setUploadedFiles(filesResp.data || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load resource bank");
    } finally {
      setLoading(false);
    }
  }, [api]);

  useEffect(() => { load(); }, [load]);

  const handleAdd = async (input) => {
    try {
      await api.post("/resource-bank", input);
      toast.success("Added to resource bank");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to add file");
    }
  };

  const handleUpdate = async (resourceId, data) => {
    try {
      await api.patch(`/resource-bank/${resourceId}`, data);
      toast.success("Resource updated");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to update");
    }
  };

  const handleRemove = async (resourceId) => {
    if (!window.confirm("Remove this file from your resource bank?")) return;
    try {
      await api.delete(`/resource-bank/${resourceId}`);
      toast.success("Removed from resource bank");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to remove");
    }
  };

  const bankFileIds = bankFiles.map((f) => f.file_id);

  return (
    <div className="bg-[var(--bg-card)] border border-[var(--border)] rounded-lg p-6" data-testid="resource-bank-manager">
      <div className="flex items-center justify-between mb-1">
        <h2 className="font-['Cormorant_Garamond'] text-lg font-semibold text-[#FF4400]">Resource Bank</h2>
        <span className="text-xs text-[var(--text-muted)]">{bankFiles.length} / 50 files</span>
      </div>
      <p className="text-sm text-[var(--text-muted)] mb-6">
        Add reference files that get injected into every generation prompt. Toggle "always include" for files you want in every generation.
      </p>

      <AnimatePresence>
        {showAdd && <AddFilePanel uploadedFiles={uploadedFiles} bankFileIds={bankFileIds} onAdd={handleAdd} onClose={() => setShowAdd(false)} />}
      </AnimatePresence>

      {!showAdd && (
        <button onClick={() => setShowAdd(true)} data-testid="add-resource-btn"
          className="w-full mb-4 flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg border border-dashed border-[var(--border-strong)] text-[var(--text-muted)] hover:text-[#FF4400] hover:border-[#FF4400]/40 text-sm transition-colors">
          <Plus size={16} />Add File to Resource Bank
        </button>
      )}

      {loading ? (
        <div className="flex justify-center py-12"><SpinnerGap size={28} className="animate-spin text-[#FF4400]" /></div>
      ) : bankFiles.length === 0 ? (
        <div className="text-center py-12 text-[var(--text-muted)]" data-testid="resource-bank-empty">
          <Archive size={36} className="mx-auto mb-3 opacity-40" />
          <p className="text-sm">Your resource bank is empty.</p>
          <p className="text-xs mt-1">Upload files from any builder page, then add them here.</p>
        </div>
      ) : (
        <div className="space-y-3">
          <AnimatePresence>
            {bankFiles.map((r) => <ResourceCard key={r.resource_id} resource={r} onUpdate={handleUpdate} onRemove={handleRemove} />)}
          </AnimatePresence>
        </div>
      )}
    </div>
  );
};

export default ResourceBankManager;
